import { useRef, useState } from "react";

import { usePasskey } from "@/lib/hooks/use-passkey";
import { cn } from "@/lib/utils";

import CheckButton from "components/common/CheckButton";
import Input from "components/common/Forms/Input";

import DynamicResponsiveImage from "@/components/common/DynamicResponsiveImage";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { parsePhoneNumberFromString } from "@/lib/data/phone";
import { useAuth } from "@/lib/hooks/use-auth";
import { getCountryCode } from "lib/browser/device";
import { CountryCode } from "libphonenumber-js";
import { KeyRoundIcon, MailIcon, SmartphoneIcon } from "lucide-react";
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ERROR_CODES } from "./error-codes";
import { SignInWithGoogleButton } from "./sign-in-with-google-button";
import { SignInWithMicrosoftButton } from "./sign-in-with-microsoft-button";

const webauthnerrors = [
  "invalid user credential",
  "ceremony was sent an abort signal",
  "cancelled by the user",
];

export const LoginForm = ({
  currentUser,
  onLoginSuccess,
  startLogin,
  finishLogin,
  isRedirecting,
  errorMessage,
  setErrorMessage,
}: {
  currentUser: {
    id: string;
    firstName: string;
    lastName: string;
    photo?: { id: string; type: string; mimeType: string; url: string };
  } | null;
  onLoginSuccess: () => void;
  startLogin: ReturnType<typeof useAuth>["startLogin"];
  finishLogin: ReturnType<typeof useAuth>["finishLogin"];
  isRedirecting: boolean;
  errorMessage: string;
  setErrorMessage: (errorMessage: string) => void;
}) => {
  const fullName = `${currentUser?.firstName} ${currentUser?.lastName}`;
  const loading =
    startLogin.isPending || finishLogin.isPending || isRedirecting;

  const formRef = useRef<HTMLFormElement>(null);
  const { authenticate: authenticatePasskeyMutation } = usePasskey();
  const [data, setData] = useState("");

  const [valid, setValid] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [showCurrentUserConfirmation, setShowCurrentUserConfirmation] =
    useState(Boolean(currentUser?.id && !searchParams.get("token")));

  const handleInput = (e) => {
    setData(e.currentTarget.value);
    setErrorMessage("");
  };

  const handleKeys = (e) => {
    if (e.key === "Enter" && handleValidate()) {
      formRef.current?.submit();
    }
  };

  const reset = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setData("");
    setValid(false);
    setErrorMessage("");
    startLogin.reset();
    finishLogin.reset();
    authenticatePasskeyMutation.reset();
  };

  const handleSubmitWithoutWebauthn = (e) => {
    e.preventDefault();
    formRef.current?.submit();
  };

  const handleValidate = () => {
    if (!data) return false;
    setErrorMessage("");
    const emailRegex = new RegExp(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );

    // get most likely country code based on browser

    const phone = parsePhoneNumberFromString(
      data,
      getCountryCode() as CountryCode,
    );
    const isPhone = phone?.isValid();
    const isEmail = emailRegex.test(data);
    if (isPhone) {
      const isUS = phone!.country === "US";
      setData(isUS ? phone!.formatNational() : phone!.formatInternational());
      setValid(true);
      return true;
    } else if (isEmail) {
      setValid(true);
      return true;
    }

    if (!isPhone && !isEmail) {
      setErrorMessage(ERROR_CODES.INVALID_PHONE_NUMBER_OR_EMAIL);
      setValid(false);
      return false;
    }

    return false;
  };

  const type = data.includes("@") ? "email" : "phone";

  const loginStartSuccessMessage = startLogin.data?.message;

  const loginFinishSuccessMessage = finishLogin.data?.message;

  const successMessage = loginFinishSuccessMessage || loginStartSuccessMessage;

  const loginMethodOptions =
    // @ts-expect-error - TODO: fix Error type
    startLogin.error?.cause?.data?.verificationMethods ?? [];

  const loginOrganizations =
    // @ts-expect-error - TODO: fix Error type
    startLogin.error?.cause?.data?.organizations ?? [];

  const disableSubmitButton =
    data === "" ||
    loading ||
    successMessage ||
    (loginOrganizations.length > 0 && !searchParams.get("organization_id"));

  const emailPayload = type === "email" ? data.toLowerCase() : undefined;
  const phonePayload =
    type === "phone"
      ? parsePhoneNumberFromString(data, getCountryCode() as CountryCode)
          ?.number
      : undefined;

  return (
    <>
      <form
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault();
          if (!handleValidate()) {
            return;
          }
          startLogin.mutate(
            {
              data: {
                email: emailPayload,
                phone: phonePayload,
                organizationId: searchParams.get("organization_id"),
              },
              query: Object.fromEntries(searchParams.entries()),
            },
            {
              onError: (error) => {
                setErrorMessage(error.message);
              },
            },
          );
        }}
        className="w-full"
      >
        <p
          className={cn(
            "rounded-md text-sm transition-all",
            errorMessage || successMessage
              ? "mb-2 p-4 opacity-100"
              : "opacity-0",
            errorMessage && "bg-pink",
            successMessage && "bg-green",
          )}
        >
          {errorMessage || successMessage}
          {webauthnerrors.some((err) =>
            errorMessage?.toLowerCase().includes(err),
          ) && (
            <button
              className="ml-2 underline"
              name="skipWebauthn"
              onClick={handleSubmitWithoutWebauthn}
            >
              Send me a link.
            </button>
          )}
        </p>
        {!showCurrentUserConfirmation ? (
          <div className="mb-4">
            <Input
              // @ts-expect-error - TODO: fix Input type
              required={true}
              name="data"
              value={data}
              type="text"
              className="rounded-md"
              label="Enter Mobile Number or Email"
              onChange={handleInput}
              onBlur={handleValidate}
              onKeyUp={handleKeys}
              valid={valid}
              disabled={
                successMessage || loading || loginMethodOptions.length > 0
              }
              spellCheck={false}
              autoFocus={true}
            />

            <div className={cn("mt-2 transition-all")}>
              {loginOrganizations.length > 0 && (
                <ScrollArea className="h-48">
                  {loginOrganizations.map((opt) => {
                    return (
                      <CheckButton
                        key={opt.id}
                        id={opt.id}
                        active={searchParams.get("organization_id") === opt.id}
                        label={opt.name}
                        onChange={(id) =>
                          navigate({
                            pathname: "login",
                            search: createSearchParams({
                              organization_id: id,
                            }).toString(),
                          })
                        }
                        className="border-b border-b-[#353941] px-2 text-white last:border-b-0"
                      />
                    );
                  })}
                </ScrollArea>
              )}
              <div className="flex flex-col gap-2">
                {finishLogin.isPending
                  ? null
                  : loginMethodOptions.map((opt) => {
                      if (opt.id === "oauthGoogle") {
                        return (
                          <SignInWithGoogleButton
                            key={opt.id}
                            loginHint={
                              type === "email" ? data.toLowerCase() : undefined
                            }
                            onError={setErrorMessage}
                            onSuccess={(accessToken) => {
                              startLogin.reset();
                              setErrorMessage("");
                              finishLogin.mutate(
                                {
                                  data: {
                                    googleCredential: accessToken,
                                    email: emailPayload,
                                    phone: phonePayload,
                                    organizationId:
                                      searchParams.get("organization_id"),
                                  },
                                },
                                {
                                  onSuccess: onLoginSuccess,
                                  onError: (error) => {
                                    setErrorMessage(error.message);
                                  },
                                },
                              );
                            }}
                          />
                        );
                      }
                      if (opt.id === "oauthMicrosoft") {
                        return (
                          <SignInWithMicrosoftButton
                            key={opt.id}
                            onError={setErrorMessage}
                            onSuccess={(accessToken) => {
                              startLogin.reset();
                              setErrorMessage("");
                              finishLogin.mutate(
                                {
                                  data: {
                                    microsoftCredential: accessToken,
                                    email: emailPayload,
                                    phone: phonePayload,
                                    organizationId:
                                      searchParams.get("organization_id"),
                                  },
                                },
                                {
                                  onSuccess: onLoginSuccess,
                                  onError: (error) => {
                                    setErrorMessage(error.message);
                                  },
                                },
                              );
                            }}
                          />
                        );
                      }
                      if (opt.id === "link" && type === "email") {
                        return (
                          <Button
                            id={opt.id}
                            name={opt.id}
                            className="relative"
                            onClick={(e) => {
                              e.preventDefault();
                              startLogin.mutate(
                                {
                                  data: {
                                    email: emailPayload,
                                    organizationId:
                                      searchParams.get("organization_id"),
                                  },
                                  query: {
                                    verificationMethod: opt.id,
                                  },
                                },
                                {
                                  onSuccess: () => setErrorMessage(""),
                                  onError: (error) => {
                                    setErrorMessage(error.message);
                                  },
                                },
                              );
                            }}
                            key={opt.id}
                          >
                            <MailIcon className="absolute left-2 top-1/2 size-4 -translate-y-1/2" />{" "}
                            Email me a Link
                          </Button>
                        );
                      }
                      if (opt.id === "link" && type === "phone") {
                        return (
                          <Button
                            id={opt.id}
                            name={opt.id}
                            type="submit"
                            className="relative"
                            key={opt.id}
                            onClick={(e) => {
                              e.preventDefault();
                              startLogin.mutate(
                                {
                                  data: {
                                    phone: phonePayload,
                                    organizationId:
                                      searchParams.get("organization_id"),
                                  },
                                  query: {
                                    verificationMethod: opt.id,
                                  },
                                },
                                {
                                  onSuccess: () => setErrorMessage(""),
                                  onError: (error) => {
                                    setErrorMessage(error.message);
                                  },
                                },
                              );
                            }}
                          >
                            <SmartphoneIcon className="absolute left-2 top-1/2 size-4 -translate-y-1/2" />{" "}
                            Text me a Link
                          </Button>
                        );
                      }

                      if (opt.id === "webauthn") {
                        return (
                          <Button
                            id={opt.id}
                            name={opt.id}
                            type="submit"
                            className="relative"
                            onClick={(e) => {
                              e.preventDefault();
                              startLogin.mutate(
                                {
                                  data: {
                                    organizationId:
                                      searchParams.get("organizationId"),
                                    email: emailPayload,
                                    phone: phonePayload,
                                  },
                                  query: {
                                    verificationMethod: opt.id,
                                  },
                                },
                                {
                                  onSuccess: async (response) => {
                                    if (response.credentialId) {
                                      try {
                                        const challengeResponse =
                                          await authenticatePasskeyMutation.mutateAsync(
                                            response.options,
                                          );
                                        finishLogin.mutate(
                                          {
                                            data: {
                                              challengeResponse,
                                              credentialId:
                                                response.credentialId,
                                              userId: response.userId,
                                            },
                                          },
                                          {
                                            onSuccess: onLoginSuccess,
                                            onError: (error) => {
                                              setErrorMessage(error.message);
                                            },
                                          },
                                        );
                                      } catch (error) {}
                                    }
                                  },
                                },
                              );
                            }}
                            key={opt.id}
                          >
                            <KeyRoundIcon className="absolute left-2 top-1/2 size-4 -translate-y-1/2" />{" "}
                            Sign in with Passkey
                          </Button>
                        );
                      }

                      return null;
                    })}
                {loginMethodOptions.length > 0 && (
                  <button
                    className="text-sm text-light-gray-100"
                    onClick={reset}
                  >
                    Start Over
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-4">
            {/* @ts-expect-error - TODO: fix Card type */}
            <Card>
              {/* @ts-expect-error - TODO: fix CardHeader type */}
              <CardHeader>
                {/* @ts-expect-error - TODO: fix CardTitle type */}
                <CardTitle className="w-full">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {currentUser?.photo && (
                        <DynamicResponsiveImage
                          src={currentUser?.photo?.url}
                          mediaFile={currentUser?.photo}
                          alt={currentUser?.firstName}
                          className="size-12 rounded-full"
                        />
                      )}
                      <h3 className="font-normal text-white">
                        Logged in as{" "}
                        <strong className="font-medium">
                          {currentUser?.firstName} {currentUser?.lastName}
                        </strong>
                      </h3>
                    </div>
                    <button
                      className="justify-self-end text-sm text-green"
                      onClick={() => setShowCurrentUserConfirmation(false)}
                    >
                      Not you?
                    </button>
                  </div>
                </CardTitle>
              </CardHeader>
            </Card>
          </div>
        )}

        {showCurrentUserConfirmation && (
          <Button type="button" className="w-full" onClick={onLoginSuccess}>
            {loading && (
              <LoadingSpinner
                width={12}
                height={12}
                className="mx-2 mt-0.5 text-black"
                fullScreen={false}
              />
            )}
            {loading ? "Logging you in" : `Continue as ${fullName}`}
          </Button>
        )}

        {loginMethodOptions.length === 0 && !showCurrentUserConfirmation && (
          <Button
            type="submit"
            className="w-full"
            disabled={Boolean(disableSubmitButton)}
          >
            {loading && (
              <LoadingSpinner
                width={12}
                height={12}
                className="mx-2 mt-0.5 text-black"
                fullScreen={false}
              />
            )}
            {loading ? "Logging you in" : "Login"}
          </Button>
        )}
      </form>
    </>
  );
};

export default LoginForm;
